/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: usePhonebooksList.tsx
 🗓️ Created: 2025-12-05
 🧩 Description:
    Hook for managing Phonebooks list with DataGrid.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useMemo, useCallback, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useContextStore } from '../../../store/context';
import { fetchPhonebooks } from '../api/phonebooksApi';
import type { Phonebook } from '../types';

interface UsePhonebooksListOptions {
  domainId?: number;
  onRefreshNeeded?: () => void;
}

export function usePhonebooksList(options: UsePhonebooksListOptions = {}) {
  const { t } = useI18n();
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = options.domainId ?? selectedDomain?.id;

  const availableColumns = useMemo(
    () => [
      {
        key: 'description',
        label: t('phonebooks.columns.description'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: Phonebook) => (
          <span className="font-medium text-foreground">{item.description}</span>
        ),
      },
      {
        key: 'type',
        label: t('phonebooks.columns.type'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: Phonebook) => (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${item.type === 'internal'
                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
              }`}
          >
            {item.type === 'internal' ? t('phonebooks.internal') : t('phonebooks.external')}
          </span>
        ),
      },
      {
        key: 'contactCount',
        label: t('phonebooks.columns.entries'),
        sortable: false,
        visible: true,
        render: (_value: unknown, item: Phonebook) => (
          <span className="text-sm text-muted-foreground">
            {item.contactCount ?? 0}
          </span>
        ),
      },
      {
        key: 'enabled',
        label: t('phonebooks.columns.status'),
        sortable: true,
        visible: true,
        render: (_value: unknown, item: Phonebook) => (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${item.enabled
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
              }`}
          >
            {item.enabled ? t('common.enabled') : t('common.disabled')}
          </span>
        ),
      },
      {
        key: 'actions',
        label: t('common.actions'),
        sortable: false,
        visible: true,
      },
    ],
    [t]
  );

  const fetchData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      const result = await fetchPhonebooks({
        ...params,
        domainId: effectiveDomainId,
      });

      return {
        data: result.data,
        pagination: result.pagination,
      };
    },
    [effectiveDomainId]
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'description',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'phonebooks',
    reloadKey: effectiveDomainId ?? null,
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && options.onRefreshNeeded) {
      options.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  const phonebooks = useMemo(() => data as Phonebook[], [data]);

  return {
    phonebooks,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    effectiveDomainId,
  };
}

